import { ariaAttributesFor, mirrorId, type AccessibleNode } from "../core/accessibility.js";
import { overlayBox } from "../core/autofill.js";
import type { Container } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import { reconcile, type MirrorPatch } from "./reconcile.js";
import { snapshotAccessibleTree, type AccessibleSnapshot } from "./snapshot.js";

/** What `AriaMirror` needs of the desktop, spelled out so a test can supply it. */
export interface AriaMirrorHost {
  readonly uiScale: number;
  /** The root of the control tree to mirror. */
  readonly root: Container;
  /** The modal holding input, if one is. */
  readonly modal: Control | null;
  /** The control Webwin considers focused - not the element the browser does. */
  readonly focused: Control | null;
  /** The element keystrokes are going to: the canvas, or a text field. */
  readonly keyboardHost: HTMLElement;
  /** The canvas itself, which the mirror has to line itself up with. */
  readonly canvas: HTMLElement;
  readonly itemPolicy: "window" | "all";
  readonly itemCap: number;
  /** Press a control the way a pointer would. */
  activate(control: Control, itemIndex: number | null): void;
}

export interface AriaMirrorOptions {
  /** How long between tree walks. Focus is exempt and always immediate. */
  snapshotIntervalMs?: number;
  /** What the whole surface is called. */
  label?: string;
}

/**
 * How often the tree is walked when something is changing.
 *
 * A reader needs the focused node right now and the rest of the tree soon.
 * Ten times a second is soon; sixty times a second is fifty-nine tree walks
 * nobody asked for, on a desktop that is only repainting a blinking caret.
 */
const DEFAULT_INTERVAL_MS = 100;

/** Roles a screen reader will try to activate, and so need a click listener. */
const ACTIVATABLE = new Set([
  "button",
  "link",
  "checkbox",
  "radio",
  "option",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
  "tab",
  "treeitem",
  "gridcell",
]);

/**
 * A hidden DOM tree shadowing the control tree, for a screen reader to read.
 *
 * The thing the README called future work. A canvas is one node with no
 * inside, so the only way an assistive technology can see a Webwin desktop is
 * if something builds it a parallel tree of real elements - and keeps that
 * tree true while windows open, lists scroll and captions change.
 *
 * Created through `Desktop.beginAccessibility` and disposed with the desktop.
 *
 * Three things about the shape of this are worth knowing before changing it.
 *
 * Nothing here decides anything. `snapshotAccessibleTree` works out what the
 * tree should contain and `reconcile` works out what changed; this applies a
 * list of patches. That split is what lets the decisions be tested in
 * `environment: "node"`, where there is no DOM at all.
 *
 * Nothing here is a tab stop. `KeyboardBridge.setTextEntry` reconciles
 * `document.activeElement` on every pointer press, so an element here that
 * took real focus would be yanked out of it immediately and forever. Focus is
 * conveyed with `aria-activedescendant` instead.
 *
 * Nothing here ever calls `invalidate`. The desktop stops painting when it is
 * clean, and one request for a frame from the mirror would pin it at sixty
 * frames a second for the life of the application.
 */
export class AriaMirror {
  readonly element: HTMLDivElement;
  #host: AriaMirrorHost;
  #interval: number;
  #elements = new Map<string, HTMLElement>();
  #entries = new Map<string, { control: Control; itemIndex: number | null }>();
  #previous: AccessibleSnapshot = [];
  #lastWalk = Number.NEGATIVE_INFINITY;
  #trailing: ReturnType<typeof setTimeout> | 0 = 0;
  #decorated: HTMLElement | null = null;
  #activeKey: string | null = null;
  #disposed = false;

  constructor(host: AriaMirrorHost, parent: HTMLElement, options: AriaMirrorOptions = {}) {
    this.#host = host;
    this.#interval = options.snapshotIntervalMs ?? DEFAULT_INTERVAL_MS;

    const root = document.createElement("div");
    root.className = "webwin-a11y";
    root.id = mirrorId("root");
    Object.assign(root.style, {
      position: "absolute",
      left: "0px",
      top: "0px",
      width: "0px",
      height: "0px",
      margin: "0",
      padding: "0",
      border: "0",
      // The canvas does all the pointing. These elements exist to be read, and
      // a screen reader activates one by dispatching a click straight at it
      // rather than by hit testing, so they still hear about it.
      pointerEvents: "none",
      zIndex: "1",
    } satisfies Partial<CSSStyleDeclaration>);
    parent.appendChild(root);
    this.element = root;

    this.onFocusChanged();
  }

  /**
   * Bring the tree up to date, at most once every `snapshotIntervalMs`.
   *
   * Called once a frame by the desktop, from the same slot the autofill forms
   * use - after layout, so bounds are settled, and before painting, so the
   * work lands in a frame that was going to happen anyway.
   */
  sync(now: number): void {
    this.#walk(now * 1000);
  }

  /**
   * Walk, or arrange to walk once the interval is up.
   *
   * The trailing timer is not an optimization, it is the difference between
   * working and not. The mirror rides the frame loop, and the desktop stops
   * painting the moment it is clean - so a change that lands inside a
   * throttled window and then leaves the desktop idle would never be
   * mirrored at all. That is not a corner case: it is what an application
   * that builds its windows at start-up and then waits for input does, every
   * time. A timer costs nothing while idle and cannot pin the frame loop,
   * which asking for a frame instead would do forever.
   */
  #walk(at: number): void {
    if (this.#disposed) return;
    const due = this.#lastWalk + this.#interval;
    if (at < due) {
      if (this.#trailing === 0) {
        this.#trailing = setTimeout(() => {
          this.#trailing = 0;
          this.#walk(performance.now());
        }, due - at);
      }
      return;
    }
    this.#lastWalk = at;
    this.#alignToCanvas();

    const next = snapshotAccessibleTree(this.#host.root, {
      modal: this.#host.modal,
      itemPolicy: this.#host.itemPolicy,
      itemCap: this.#host.itemCap,
    });
    const patches = reconcile(this.#previous, next);
    this.#previous = next;
    if (patches.length === 0) return;

    for (const entry of next) {
      this.#entries.set(entry.key, { control: entry.control, itemIndex: entry.itemIndex });
    }
    for (const patch of patches) this.#apply(patch);
    // A create may have built the element focus is meant to be pointing at.
    this.onFocusChanged();
  }

  /**
   * Point the browser at the element standing for the focused control.
   *
   * Exempt from the throttle, and called straight from the focus manager: the
   * rest of the tree can be a tenth of a second stale without anybody
   * noticing, and the focused node cannot be stale at all.
   *
   * Which element carries the attribute depends on where keystrokes are
   * going. When a text control has focus that is the hidden textarea, which
   * is a far better answer than `aria-activedescendant` could be - it holds
   * the real text and the real selection, so the browser supplies caret,
   * character and selection announcements on its own. All this has to do
   * there is name it.
   */
  onFocusChanged(): void {
    if (this.#disposed) return;
    const host = this.#host.keyboardHost;
    if (this.#decorated && this.#decorated !== host) this.#undecorate(this.#decorated);
    this.#decorated = host;

    const focused = this.#host.focused;
    const key = focused ? String(focused.id) : null;
    this.#activeKey = key;

    if (host instanceof HTMLTextAreaElement || host instanceof HTMLInputElement) {
      // The field itself is the accessible object. Name it and get out of the
      // way; anything else here would be read on top of the browser's own.
      const name = focused?.describeAccessible()?.name ?? "";
      if (name) host.setAttribute("aria-label", name);
      else host.removeAttribute("aria-label");
      host.removeAttribute("aria-activedescendant");
      host.removeAttribute("aria-owns");
      return;
    }

    // The canvas. It owns the mirror so the two read as one object, and names
    // the element standing for whatever has focus.
    host.setAttribute("role", "application");
    host.setAttribute("aria-owns", this.element.id);
    const id = key ? mirrorId(key) : null;
    if (id && this.#elements.has(key!)) host.setAttribute("aria-activedescendant", id);
    else host.removeAttribute("aria-activedescendant");
  }

  get disposed(): boolean {
    return this.#disposed;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    if (this.#trailing !== 0) clearTimeout(this.#trailing);
    this.#trailing = 0;
    if (this.#decorated) this.#undecorate(this.#decorated);
    this.#decorated = null;
    this.#elements.clear();
    this.#entries.clear();
    this.#previous = [];
    this.element.remove();
  }

  /**
   * Put the mirror's origin where the canvas's is.
   *
   * Control coordinates are measured from the top-left of the canvas, and the
   * canvas is not always at the top-left of the element the mirror is
   * appended to - a page with a nav bar above it puts it forty-odd pixels
   * down. Left unaligned every box is off by that much, which is invisible in
   * a screenshot and is exactly wrong for the one thing the boxes are for:
   * explore-by-touch, where a finger on a button has to find the button.
   */
  #alignToCanvas(): void {
    const canvas = this.#host.canvas;
    const left = `${canvas.offsetLeft}px`;
    const top = `${canvas.offsetTop}px`;
    const s = this.element.style;
    if (s.left !== left) s.left = left;
    if (s.top !== top) s.top = top;
  }

  #undecorate(host: HTMLElement): void {
    host.removeAttribute("aria-activedescendant");
    host.removeAttribute("aria-owns");
    host.removeAttribute("aria-label");
    // `role` is only ever put on the canvas, and taking it off again is what
    // leaves the page as it was found when accessibility is disposed.
    if (host instanceof HTMLCanvasElement) host.removeAttribute("role");
  }

  #apply(patch: MirrorPatch): void {
    switch (patch.op) {
      case "create": {
        const el = this.#create(patch.key, patch.node);
        this.#place(el, patch.parentKey, patch.index);
        this.#write(el, patch.node);
        this.#position(el, patch.bounds);
        this.#conceal(el, patch.hidden);
        return;
      }
      case "remove": {
        const el = this.#elements.get(patch.key);
        if (!el) return;
        // The browser takes the subtree with it, so the map has to let go of
        // every key inside as well - otherwise a later create finds a stale
        // element that is no longer in the document and writes into nothing.
        for (const child of el.querySelectorAll<HTMLElement>("[data-key]")) {
          this.#elements.delete(child.dataset["key"] ?? "");
          this.#entries.delete(child.dataset["key"] ?? "");
        }
        this.#elements.delete(patch.key);
        this.#entries.delete(patch.key);
        el.remove();
        return;
      }
      case "move": {
        const el = this.#elements.get(patch.key);
        if (el) this.#place(el, patch.parentKey, patch.index);
        return;
      }
      case "attrs": {
        const el = this.#elements.get(patch.key);
        if (el) this.#write(el, patch.node);
        return;
      }
      case "text": {
        const el = this.#elements.get(patch.key);
        if (el) el.textContent = patch.text;
        return;
      }
      case "bounds": {
        const el = this.#elements.get(patch.key);
        if (el) this.#position(el, patch.bounds);
        return;
      }
      case "hidden": {
        const el = this.#elements.get(patch.key);
        if (el) this.#conceal(el, patch.hidden);
        return;
      }
    }
  }

  #create(key: string, node: AccessibleNode): HTMLElement {
    const el = document.createElement("div");
    el.id = mirrorId(key);
    el.dataset["key"] = key;
    Object.assign(el.style, {
      position: "absolute",
      margin: "0",
      padding: "0",
      border: "0",
      overflow: "hidden",
      whiteSpace: "pre",
      pointerEvents: "none",
      // Transparent rather than invisible. `display:none`, `visibility:hidden`
      // and in some engines `opacity:0` all take the words out of find-in-page
      // as well as off the screen, and finding them is half of why they are
      // here. Transparent text is laid out, searchable and highlightable, and
      // draws nothing over the canvas.
      color: "transparent",
      background: "transparent",
      // Smaller than this and a phone offers to zoom the page.
      fontSize: "16px",
      lineHeight: "1",
    } satisfies Partial<CSSStyleDeclaration>);
    if (ACTIVATABLE.has(node.role)) el.addEventListener("click", this.#onActivate);
    this.#elements.set(key, el);
    return el;
  }

  #place(el: HTMLElement, parentKey: string | null, index: number): void {
    const parent = parentKey === null ? this.element : (this.#elements.get(parentKey) ?? this.element);
    // Checked rather than done unconditionally: re-inserting an element a
    // reader is part way through speaking can cut it off, and after a removal
    // most of the siblings the diff calls moved are already where they belong.
    if (parent.children[index] === el) return;
    parent.insertBefore(el, parent.children[index] ?? null);
  }

  #write(el: HTMLElement, node: AccessibleNode): void {
    const attributes = ariaAttributesFor(node);
    const wanted = new Set(Object.keys(attributes));
    const had = (el.dataset["aria"] ?? "").split(",").filter(Boolean);
    // Taking away what is no longer wanted matters as much as writing what is:
    // a stale aria-expanded on a menu that closed says it is still open.
    for (const name of had) if (!wanted.has(name)) el.removeAttribute(name);
    for (const [name, value] of Object.entries(attributes)) el.setAttribute(name, value);
    el.dataset["aria"] = [...wanted].join(",");
    if (node.text !== undefined) el.textContent = node.text;
  }

  #position(el: HTMLElement, bounds: { x: number; y: number; width: number; height: number }): void {
    // The same conversion the autofill fields use, for the same reason: an
    // element has to sit over the thing it stands for. Here it is what lets a
    // phone read the control under a finger, which is how TalkBack is mostly
    // used and involves no focus at all.
    const box = overlayBox(bounds, this.#host.uiScale);
    const s = el.style;
    s.left = `${box.left}px`;
    s.top = `${box.top}px`;
    s.width = `${box.width}px`;
    s.height = `${box.height}px`;
  }

  #conceal(el: HTMLElement, hidden: boolean): void {
    if (hidden) el.setAttribute("aria-hidden", "true");
    else el.removeAttribute("aria-hidden");
  }

  /**
   * A screen reader activated something.
   *
   * It reaches this listener despite `pointer-events: none` because an
   * assistive technology does not hit test - it performs the default action on
   * the accessible object, which the browser turns into a click dispatched
   * straight at the element.
   */
  #onActivate = (e: Event): void => {
    const key = (e.currentTarget as HTMLElement | null)?.dataset["key"];
    const entry = key ? this.#entries.get(key) : undefined;
    if (!entry) return;
    e.preventDefault();
    this.#host.activate(entry.control, entry.itemIndex);
  };
}
